import React, { ReactElement } from 'react';
import {
  ConversationErrorCode,
  ConversationStatus,
  MeetingBookingStatus,
  NeedsReviewReason
} from 'generated-graphql/types';
import {
  AccessTime,
  Check,
  DoNotDisturb,
  Error,
  EventBusy,
  PanoramaFishEye,
  Square
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Box, SxProps, Typography } from '@mui/material';
import { IconTooltip, PulsingDot } from 'common/ui';
import {
  acceptedStatuses,
  completedStatuses,
  rejectedStatuses
} from 'modules/conversations/constants/statuses';
import { errorCodeToTooltip } from 'modules/conversations/utils';
import { getCampaignDisplayStatus } from 'modules/conversations/utils/getCampaignDisplayStatus';
import { CampaignType } from 'services/campaign/types';
import { ReviewIcon } from 'common/icons/ReviewIcon';

type StatusIconContainerProps = {
  withLabel: boolean;
  label: string;
  tooltipTitle: string;
  children: ReactElement;
};

const statusIconContainerStyles: SxProps = {
  display: 'grid',
  gridTemplateColumns: '20px auto',
  alignItems: 'center',
  gap: 1
};

const iconContainerStyles: SxProps = {
  width: 20,
  height: 20,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

const StatusIconContainer: React.FC<StatusIconContainerProps> = ({
  children,
  withLabel,
  label,
  tooltipTitle
}) => {
  if (withLabel) {
    return (
      <Box sx={statusIconContainerStyles}>
        <Box sx={iconContainerStyles}>{children}</Box>
        <Typography variant="body2">{label}</Typography>
      </Box>
    );
  }
  return <IconTooltip title={tooltipTitle}>{children}</IconTooltip>;
};

interface StatusConfig {
  label: string;
  tooltipTitle: string;
  icon: () => ReactElement;
}

export const STATUS_CONFIG: Record<string, StatusConfig> = {
  moreTimeRequested: {
    label: 'More time requested',
    tooltipTitle: 'Candidate has requested additional time slots',
    icon: () => (
      <EventBusy
        sx={{
          fontSize: '20px',
          color: (theme) => theme.palette.warning.dark
        }}
      />
    )
  },
  review: {
    label: 'Popp needs your help',
    tooltipTitle: 'Popp needs your help',
    icon: () => <ReviewIcon />
  },
  [ConversationStatus.Ongoing]: {
    label: 'In progress',
    tooltipTitle: 'In progress',
    icon: () => (
      <Box>
        <PulsingDot size={8} />
      </Box>
    )
  },
  [ConversationStatus.Created]: {
    label: 'Created',
    tooltipTitle: 'Created',
    icon: () => (
      <PanoramaFishEye
        sx={{
          fontSize: '8px',
          color: (theme) => theme.palette.success.dark
        }}
      />
    )
  },
  [ConversationStatus.Queued]: {
    label: 'Queued',
    tooltipTitle: 'Queued until their active conversation closes',
    icon: () => (
      <AccessTime
        sx={{
          fontSize: '20px',
          color: (theme) => theme.palette.warning.dark
        }}
      />
    )
  },
  [ConversationStatus.DeliveryFailed]: {
    label: 'Not delivered',
    tooltipTitle: 'Not delivered. Check contact details',
    icon: () => (
      <Error
        sx={{
          fontSize: '20px',
          color: 'error.main'
        }}
      />
    )
  },
  [ConversationStatus.OptedOut]: {
    label: 'Opted out',
    tooltipTitle: 'Opted out',
    icon: () => (
      <DoNotDisturb
        sx={{
          fontSize: '20px',
          color: (theme) => theme.palette.error.main
        }}
      />
    )
  },
  accepted: {
    label: 'Completed & satisfied',
    tooltipTitle: 'Completed & satisfied',
    icon: () => (
      <Check
        sx={{
          fontSize: '20px',
          color: (theme) => theme.palette.successV2.main
        }}
      />
    )
  },
  rejected: {
    label: 'Completed & unsatisfied',
    tooltipTitle: 'Completed & unsatisfied',
    icon: () => (
      <CloseIcon
        sx={{
          fontSize: '20px',
          color: (theme) => theme.palette.error.main
        }}
      />
    )
  },
  closed: {
    label: 'Closed',
    tooltipTitle: 'Closed',
    icon: () => (
      <Square
        sx={{
          fontSize: '8px',
          color: (theme) => theme.palette.text.disabled
        }}
      />
    )
  }
};

// AppSync types `unresolvedNeedsReviewReasons` as `[String!]` on the wire, so
// callers hand this in as `string[] | null | undefined`. Narrowing happens at
// read time via `includes(NeedsReviewReason.<...>)`; a wider input prop keeps
// consumers off the boilerplate of casting at every callsite.
type UnresolvedReasons = readonly string[] | null;

type Props = {
  status: ConversationStatus;
  needsReview: boolean;
  unresolvedNeedsReviewReasons?: UnresolvedReasons;
  withLabel?: boolean;
  errorCode?: ConversationErrorCode | null;
  campaignType?: CampaignType | null;
  meetingBookingStatus?: MeetingBookingStatus | null;
};

export const hasMoreTimeRequested = (
  unresolvedNeedsReviewReasons?: UnresolvedReasons
): boolean =>
  Array.isArray(unresolvedNeedsReviewReasons) &&
  unresolvedNeedsReviewReasons.includes(NeedsReviewReason.MoreTimeRequested);

export const getStatusConfig = (
  status: ConversationStatus,
  needsReview: boolean,
  unresolvedNeedsReviewReasons?: UnresolvedReasons,
  errorCode?: ConversationErrorCode | null
) => {
  let configKey: string;

  if (hasMoreTimeRequested(unresolvedNeedsReviewReasons)) {
    configKey = 'moreTimeRequested';
  } else if (needsReview) {
    configKey = 'review';
  } else if (acceptedStatuses.includes(status)) {
    configKey = 'accepted';
  } else if (rejectedStatuses.includes(status)) {
    configKey = 'rejected';
  } else if (completedStatuses.includes(status)) {
    configKey = 'closed';
  } else {
    configKey = status;
  }

  const config = STATUS_CONFIG[configKey];
  if (!config) return config;

  // For DELIVERY_FAILED conversations the underlying errorCode tells us
  // whether the contact info was the problem or the carrier / mail
  // server rejected the message. The tooltip mirrors that distinction
  // so operators don't chase contact-data issues for delivery-side
  // failures (PAD-5118 / PAD-5119).
  //
  // Gate on `configKey` rather than `status` so the override only fires
  // when the icon/label actually render the DeliveryFailed config —
  // i.e. when needsReview hasn't already redirected the row to the
  // review icon. A status=DeliveryFailed + needsReview=true row shows
  // the review icon, so its tooltip must stay "Popp needs your help".
  if (configKey === ConversationStatus.DeliveryFailed) {
    return { ...config, tooltipTitle: errorCodeToTooltip(errorCode) };
  }

  return config;
};

export const ConversationStatusIconAndLabel: React.FC<Props> = ({
  status,
  needsReview,
  unresolvedNeedsReviewReasons,
  withLabel = false,
  errorCode,
  campaignType,
  meetingBookingStatus
}) => {
  const config = getStatusConfig(
    getCampaignDisplayStatus({ status, campaignType, meetingBookingStatus }),
    needsReview,
    unresolvedNeedsReviewReasons,
    errorCode
  );

  if (!config) return <div />;

  return (
    <StatusIconContainer
      withLabel={withLabel}
      label={config.label}
      tooltipTitle={config.tooltipTitle}
    >
      {config.icon()}
    </StatusIconContainer>
  );
};
